import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  TableCell,
  Box,
} from "@mui/material";
import { ExpandMore, Group } from "@mui/icons-material";
import {
  deleteTrainingParticipant,
  getParticipantsByCourseNumber,
} from "../../../reducers/TrainingParticipantReducer";
import PATAddButton from "../../../components/Buttons/PATAddButton";
import PATEditButton from "../../../components/Buttons/PATEditButton";
import PATDeleteButton from "../../../components/Buttons/PATDeleteButton";
import CustomTable from "../../../components/CustomTable";
import DeleteConfirmationDialog from "../../../components/DeleteConfirmationDialog";
import {
  PATH_TRAINING_COURSES_EDIT,
  PATH_PARTICIPANTS_ADD,
  PATH_PARTICIPANTS_EDIT,
} from "../../../utils/routeConstants";

const tableHeaders = ["First Name", "Last Name", "Grade", "Actions"];

const CourseParticipantTable = ({
  id,
  theme,
  courseNumber,
  courseParticipants,
  allExpanded = false,
  canAdd,
  canEdit,
  canDelete,
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { userData } = useSelector((state) => state.user);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [participantToDelete, setParticipantToDelete] = useState(null);

  const [expanded, setExpanded] = useState(allExpanded);

  useEffect(() => {
    setExpanded(allExpanded);
  }, [allExpanded]);

  const handleOpenDeleteDialog = (e, participantId) => {
    e.stopPropagation();
    setParticipantToDelete(participantId);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (participantToDelete) {
      dispatch(
        deleteTrainingParticipant(
          participantToDelete,
          userData?.user?.ptsUserID
        )
      ).then(() => dispatch(getParticipantsByCourseNumber(courseNumber)));
    }
    setOpenDeleteDialog(false);
    setParticipantToDelete(null);
  };

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded((prev) => !prev)}
        sx={{ mb: 1 }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            backgroundColor: theme.accordion.backgroundColor,
            color: theme.accordion.textColor,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Box display="flex" alignItems="center">
              <Group sx={{ mr: 1 }} />
              <span>Participants</span>
            </Box>
            <Box sx={{ mr: 2 }}>
              {canAdd && (
                <PATAddButton
                  tooltipText="Add Participant"
                  onClick={() =>
                    history.push(
                      `${PATH_TRAINING_COURSES_EDIT}${id}${PATH_PARTICIPANTS_ADD}`,
                      { fromCourse: true }
                    )
                  }
                />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {courseParticipants && courseParticipants.length > 0 ? (
            <CustomTable
              columns={tableHeaders}
              data={courseParticipants}
              renderRow={(participant, index) => (
                <TableRow key={index}>
                  <TableCell>{participant.firstName}</TableCell>
                  <TableCell>{participant.lastName}</TableCell>
                  <TableCell>{participant.grade}</TableCell>
                  <TableCell>
                    <Box display="flex" gap={1}>
                      {canEdit && (
                        <PATEditButton
                          tooltipText="Edit Participant"
                          onClick={() =>
                            history.push(
                              `${PATH_TRAINING_COURSES_EDIT}${id}${PATH_PARTICIPANTS_EDIT}${participant.id}`,
                              { fromCourse: true }
                            )
                          }
                          size="20px"
                        />
                      )}
                      {canDelete && (
                        <PATDeleteButton
                          tooltipText="Delete Participant"
                          onClick={(e) =>
                            handleOpenDeleteDialog(e, participant.id)
                          }
                          size="20px"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            />
          ) : (
            <span>No Participants Available</span>
          )}
        </AccordionDetails>
      </Accordion>
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setParticipantToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Participant"
        message="Are you sure you want to delete this participant?"
      />
    </>
  );
};

export default CourseParticipantTable;
